import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "form-step-title"
}
const _hoisted_2 = { class: "rd-form-container rd-w-full" }
const _hoisted_3 = {
  key: 1,
  class: "rd-mt-10 rd-w-full submit-container"
}
const _hoisted_4 = {
  key: 1,
  class: "rd-items-center rd-flex rd-flex-row rd-w-full"
}
const _hoisted_5 = {
  key: 1,
  class: "layout-row"
}

import { AllComponents } from "@/builder/client/AllComponents";
import ButtonComponent from "@/builder/components/button/ButtonComponent.vue";
import ConfirmationRenderContainer from "@/builder/components/form/ConfirmationRenderContainer.vue";
import PromptRenderContainer from "@/builder/components/prompt/PromptRenderContainer.vue";
import { ComponentType, PartialDisplayType } from "@/builder/enums";
import { Gift, IConfirmationComponent, IFormComponent } from "@/builder/interfaces";
import { useRenderStore } from "@/builder/stores/renderStore";
import { IntegrationType } from "@/enums";
import { googleAnalyticsService } from "@/services/googleAnalyticsService";
import { publicApiService } from "@/services/publicApiService";
import { TrackingEventType, trackingPixelService } from "@/services/trackingPixelService";
import { PropType, computed, inject, onBeforeMount, onMounted, ref, watch } from "vue";
import FormModal from "../modal/FormModal.vue";
import TrustCenterContainer from "../trustCenter/TrustCenterContainer.vue";
import FormComponent from "./FormComponent.vue";

//props and services

export default /*@__PURE__*/_defineComponent({
  __name: 'FormRenderContainer',
  props: {
    component: {
        type: Object as PropType<IFormComponent>,
        required: true
    }
},
  setup(__props) {

const props = __props;

const renderStore = useRenderStore();
const trackingPixel = inject<trackingPixelService>("trackingPixelService") as trackingPixelService;
const googleAnalytics = inject<googleAnalyticsService>("googleAnalyticsService") as googleAnalyticsService;
const publicApi = inject<publicApiService>("publicApiService") as publicApiService;

// refs
const scrollTop = ref();
const trackingPixelUrl = renderStore.trackingPixelUrl;
const trackingPixelLoaded = ref(false);
const isMounted = ref(false);

// computed
const hasVirtuousIntegration = computed(() => renderStore.hasIntegration(IntegrationType.Virtuous));

const useTrackingPixel = computed(() => {
    return renderStore.page.useTrackingPixel && hasVirtuousIntegration.value && trackingPixelLoaded.value;
});

const hasGoogleAnalyticsIntegration = computed(() => renderStore.hasIntegration(IntegrationType.GoogleAnalytics));

const currentStepTitle = computed(() => {
    const currentStep = renderStore.getFormStepByIndex(renderStore.step);
    return currentStep?.customData.displayTitle ? currentStep.customData.title : null;
});

const confirmationComponent = computed(() => {
    return props.component.components.find(x => x.type === ComponentType.Confirmation) as IConfirmationComponent;
});

// methods
const initTrackingPixel = () => {
    if (!trackingPixelUrl) {
        console.error("Virtuous tracking pixel URL not found");
        return;
    }

    //if tracking pixel script already exists, do not inject again
    if (document.getElementById("virtuous-tracking-pixel")) {
        trackingPixelLoaded.value = true;
        return;
    }

    const virtuousIntegration = renderStore.getIntegration(IntegrationType.Virtuous);
    const config = { organizationId: virtuousIntegration?.publicKey };

    //inject tracking pixel script
    const script = document.createElement("script");
    script.async = true;
    script.id = "virtuous-tracking-pixel";
    script.type = "text/javascript";
    script.src = trackingPixelUrl;

    script.onload = () => {
        // @ts-ignore
        // eslint-disable-next-line
        virtuousTrackerShim.init(config)
    };

    document.head.appendChild(script);

    trackingPixelLoaded.value = true;
}

const initGoogleAnalytics = async () => {
    return new Promise<string | null>((resolve) => {
        const googleAnalyticsIntegration = renderStore.getIntegration(IntegrationType.GoogleAnalytics);

        if (!googleAnalyticsIntegration || !googleAnalytics) {
            resolve(null);
            return;
        }

        if (googleAnalyticsIntegration?.publicKey) {
            const script = document.createElement('script');
            script.async = true;
            script.setAttribute("src", "https://www.googletagmanager.com/gtag/js?id=" + googleAnalyticsIntegration.publicKey);
            document.head.appendChild(script);

            script.onload = async () => {
                const clientId = await googleAnalytics.init(googleAnalyticsIntegration.publicKey);
                resolve(clientId);
            }

            const dataLayerScript = document.createElement('script');
            dataLayerScript.appendChild(document.createTextNode(`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${googleAnalyticsIntegration.publicKey}', { 'send_page_view': false });
            `));
            document.head.appendChild(dataLayerScript);
        } else {
            resolve(null);
        }
    });
};

const scrollToTopOfForm = () => {
    scrollTop.value.scrollIntoView({ block: "start", behavior: "smooth" });
};

//lifecycle hooks
onBeforeMount(() => {
    renderStore.setMaxSteps(props.component.components.filter(x => x.type === ComponentType.FormStep).length - 1);

    if (renderStore.page.useTrackingPixel && hasVirtuousIntegration.value) {
        initTrackingPixel();
    }
});

onMounted(async () => {
    if (renderStore.previewMode) return;

    if (useTrackingPixel.value && trackingPixel) {
        trackingPixel?.trackEvent(TrackingEventType.FormLoad, {
            formId: renderStore.page.id?.toString() ?? "",
            formName: renderStore.page.name,
        });
    }

    renderStore.clientId = await initGoogleAnalytics();

    if (hasGoogleAnalyticsIntegration.value && googleAnalytics) {
        renderStore.googleUtm = googleAnalytics.getUtmCodes();

        await googleAnalytics.trackPageView(
            renderStore.pageResponse.publicId ?? "",
            renderStore.organizationId,
            publicApi);
    }

    isMounted.value = true;
});

//watch renderstore donor for form start
watch(renderStore.donor, async (newDonor) => {
    if (newDonor && !renderStore.formStarted && isMounted.value) {
        renderStore.formStarted = true;

        if (useTrackingPixel.value && trackingPixel) {
            trackingPixel.trackEvent(TrackingEventType.FormStart, {
                formId: renderStore.pageResponse.publicId?.toString() ?? "",
                formName: renderStore.page.name,
            });
        }

        if (hasGoogleAnalyticsIntegration.value && googleAnalytics) {
            await googleAnalytics.trackFormStarted(
                renderStore.pageResponse.publicId ?? "",
                renderStore.organizationId,
                publicApi);
        }
    }
}, { deep: true });

//watch renderStore gift for form start
watch(renderStore.gift, async (newGift: Gift) => {
    if (newGift && newGift.amount && newGift.amount !== '0.00' && !renderStore.formStarted && isMounted.value) {
        renderStore.formStarted = true;

        if (useTrackingPixel.value && trackingPixel) {
            trackingPixel.trackEvent(TrackingEventType.FormStart, {
                formId: renderStore.pageResponse.publicId?.toString() ?? "",
                formName: renderStore.page.name,
            });
        }

        if (hasGoogleAnalyticsIntegration.value && googleAnalytics) {
            await googleAnalytics.trackFormStarted(
                renderStore.pageResponse.publicId ?? "",
                renderStore.organizationId,
                publicApi);
        }
    }
}, { deep: true });

watch(renderStore.errors, async (newErrors) => {
    if (newErrors.length === 0) return;
    if (renderStore.modelState && newErrors.length >= Object.keys(renderStore.modelState).length) {
        renderStore.moveToErrorStep();
    }
});

return (_ctx: any,_cache: any) => {
  return (_unref(renderStore).page.partialDisplayType === _unref(PartialDisplayType).Modal)
    ? (_openBlock(), _createBlock(FormModal, {
        key: 0,
        component: props.component,
        "organization-id": _unref(renderStore).organizationId
      }, null, 8, ["component", "organization-id"]))
    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
        _createElementVNode("form", {
          name: "rd-form",
          method: "post",
          class: _normalizeClass(["rd-form-wrapper form-wrapper", [__props.component.styles?.boxShadow, __props.component.styles?.cssClass]]),
          ref_key: "scrollTop",
          ref: scrollTop,
          style: _normalizeStyle({
                backgroundColor: __props.component.styles.backgroundColor || '',
                borderColor: __props.component.styles.outlineColor || '',
                borderRadius: __props.component.styles.borderRadius + 'px' || '',
                borderWidth: __props.component.styles.outlineWeight + 'px' || '',
                color: __props.component.styles.fontColor || '',
                padding: __props.component.styles.padding + 'px' || ''
            })
        }, [
          _createElementVNode("div", {
            class: _normalizeClass({ 'rd-items-center rd-flex rd-flex-row rd-w-full rd-mb-6': _unref(renderStore).step > 0 && !_unref(renderStore).success || currentStepTitle.value })
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.component.components.filter(x => x.type === _unref(ComponentType).FormStep), (item, index) => {
              return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                (index === _unref(renderStore).step)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      (_unref(renderStore).step > 0 && !_unref(renderStore).success)
                        ? (_openBlock(), _createBlock(ButtonComponent, {
                            key: 0,
                            "aria-label": "Go to previous step",
                            component: item.customData.buttons.prev,
                            class: "rd-btn-form"
                          }, null, 8, ["component"]))
                        : _createCommentVNode("", true)
                    ], 64))
                  : _createCommentVNode("", true)
              ], 64))
            }), 128)),
            (currentStepTitle.value && !_unref(renderStore).success)
              ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                  _createElementVNode("span", null, _toDisplayString(currentStepTitle.value), 1)
                ]))
              : _createCommentVNode("", true)
          ], 2),
          (!_unref(renderStore).success)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createElementVNode("section", _hoisted_2, [
                  _createVNode(FormComponent, {
                    component: props.component
                  }, {
                    children: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.component.components.filter(x => x.type === _unref(ComponentType).FormStep), (item, index) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: index,
                          class: _normalizeClass(index === _unref(renderStore).step ? 'rd-block rd-w-full' : 'rd-hidden')
                        }, [
                          (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(AllComponents)[item.type]), { component: item }, null, 8, ["component"]))
                        ], 2))
                      }), 128))
                    ]),
                    _: 1
                  }, 8, ["component"])
                ]),
                (_unref(renderStore).maxSteps > 0)
                  ? (_openBlock(), _createElementBlock("section", {
                      key: 0,
                      class: _normalizeClass(["rd-flex rd-justify-end rd-w-full", { 'rd-mt-6': _unref(renderStore).step !== _unref(renderStore).maxSteps }])
                    }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.component.components.filter(x => x.type === _unref(ComponentType).FormStep), (item, index) => {
                        return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                          (index === _unref(renderStore).step)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass(["rd-flex rd-w-full", item.customData.buttons.next.customData.alignment && `rd-justify-${item.customData.buttons.next.customData.alignment}`])
                              }, [
                                (_unref(renderStore).step !== _unref(renderStore).maxSteps)
                                  ? (_openBlock(), _createBlock(ButtonComponent, {
                                      key: 0,
                                      onClick: scrollToTopOfForm,
                                      component: item.customData.buttons.next,
                                      disabled: _unref(renderStore).stepHasErrors(_unref(renderStore).step)
                                    }, null, 8, ["component", "disabled"]))
                                  : _createCommentVNode("", true)
                              ], 2))
                            : _createCommentVNode("", true)
                        ], 64))
                      }), 128))
                    ], 2))
                  : _createCommentVNode("", true),
                (_unref(renderStore).step === _unref(renderStore).maxSteps)
                  ? (_openBlock(), _createElementBlock("section", _hoisted_3, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["rd-flex", `rd-justify-${__props.component.customData.buttons.submit.customData.alignment}`])
                      }, [
                        _createVNode(ButtonComponent, {
                          component: __props.component.customData.buttons.submit,
                          disabled: _unref(renderStore).canNotSubmit
                        }, {
                          buttonText: _withCtx(() => [
                            _createTextVNode(_toDisplayString(__props.component.customData.buttons.submit.customData.text) + " " + _toDisplayString(_unref(renderStore).submitButtonAmount), 1)
                          ]),
                          _: 1
                        }, 8, ["component", "disabled"])
                      ], 2)
                    ]))
                  : _createCommentVNode("", true)
              ], 64))
            : (_openBlock(), _createElementBlock("section", _hoisted_4, [
                (props.component.components.filter(x => x.type === _unref(ComponentType).Confirmation).length > 0)
                  ? (_openBlock(), _createBlock(ConfirmationRenderContainer, {
                      key: 0,
                      component: confirmationComponent.value
                    }, null, 8, ["component"]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[0] || (_cache[0] = [
                      _createElementVNode("div", { class: "layout-col rd-w-full" }, [
                        _createElementVNode("h1", null, "Thank you for your donation!")
                      ], -1)
                    ])))
              ])),
          _createVNode(TrustCenterContainer, {
            page: _unref(renderStore).page
          }, null, 8, ["page"])
        ], 6),
        _createVNode(PromptRenderContainer)
      ], 64))
}
}

})